import { useEffect, useState } from 'react'
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { Button, Card, PageHead, StateBlock } from '@w7/ui'
import { getAuthConfig, isOidcConfigured } from '@/auth/config'
import { beginKcAction, isDemoSession } from '@/auth/oidc'
import {
  activePrimaryMfaId,
  canonicalMfaMethodId,
  isPrimaryMfaMethod,
  rivalPrimaryMfaId,
} from '@/auth/mfaStatus'
import { webAuthnNeedsLocalhost } from '@/auth/webauthnHost'
import { useMfaStatus } from '@/api/hooks/shared/useMfaStatus'
import { toastCopy, useAppToast } from '@/ui/toaster'
import { getMfaMethod } from './mfaMethods'

/**
 * W7-branded enroll wizard. Crypto/QR/fingerprint ceremony stays on Keycloak AIA;
 * this page owns copy, steps, and return to /settings/security.
 */
export function MfaEnrollPage() {
  const { methodId } = useParams<{ methodId: string }>()
  const [search] = useSearchParams()
  const method = getMfaMethod(methodId)
  const navigate = useNavigate()
  const toast = useAppToast()
  const statusQuery = useMfaStatus()
  const [busy, setBusy] = useState(false)
  const demo = isDemoSession()
  const justBack = search.get('done') === '1'
  const enrolledReturn = search.get('enrolled') === methodId
  const isWebAuthn =
    method?.kcAction === 'webauthn-register' ||
    methodId === 'passkey' ||
    methodId === 'fingerprint' ||
    methodId === 'security-key'
  const passkeyHostBroken =
    Boolean(isWebAuthn) &&
    webAuthnNeedsLocalhost(
      typeof window !== 'undefined' ? window.location.hostname : 'localhost',
      getAuthConfig().keycloakUrl || undefined,
    )
  const primaryOn = activePrimaryMfaId(statusQuery.data?.activeMethodIds ?? [])
  const rivalId =
    methodId && isPrimaryMfaMethod(methodId) ? rivalPrimaryMfaId(methodId) : null
  const blockedByRival =
    Boolean(rivalId) &&
    primaryOn === rivalId &&
    canonicalMfaMethodId(methodId!) !== primaryOn
  const rivalTitle = rivalId ? getMfaMethod(rivalId)?.title ?? rivalId : ''

  // Continue → Keycloak sets busy=true; browser Back restores bfcache with busy stuck.
  useEffect(() => {
    setBusy(false)
    const clearBusy = () => setBusy(false)
    const onPageShow = (event: PageTransitionEvent) => {
      if (event.persisted) clearBusy()
    }
    const onVisibility = () => {
      if (document.visibilityState === 'visible') clearBusy()
    }
    window.addEventListener('pageshow', onPageShow)
    window.addEventListener('focus', clearBusy)
    document.addEventListener('visibilitychange', onVisibility)
    return () => {
      window.removeEventListener('pageshow', onPageShow)
      window.removeEventListener('focus', clearBusy)
      document.removeEventListener('visibilitychange', onVisibility)
    }
  }, [methodId])

  if (!method) {
    return (
      <StateBlock
        icon="search"
        title="Unknown security method"
        actions={
          <Button variant="secondary" onClick={() => navigate('/settings/security')}>
            Back to two-factor authentication
          </Button>
        }
      >
        That setup link is not recognised.
      </StateBlock>
    )
  }

  const start = async () => {
    if (!method.kcAction) return
    if (demo) {
      toast.show(toastCopy.mfaNeedKeycloak)
      navigate('/login')
      return
    }
    if (!isOidcConfigured()) {
      toast.show(toastCopy.mfaNotConfigured)
      return
    }
    if (blockedByRival) {
      toast.show({
        tone: 'warn',
        message: `Turn off ${rivalTitle} first`,
        detail: 'Only one of Authenticator or Passkey can be on.',
      })
      return
    }
    if (passkeyHostBroken) {
      toast.show(toastCopy.mfaPasskeyHost)
      return
    }
    setBusy(true)
    try {
      await beginKcAction(method.kcAction, `/settings/security?enrolled=${method.id}`)
      // location.assign should unload this page; if we’re still here, unlock the button.
      setBusy(false)
    } catch (e) {
      setBusy(false)
      toast.mfaEnrollError(e instanceof Error ? e.message : 'Try again.')
    }
  }

  return (
    <>
      <PageHead
        title={method.title}
        subtitle="Branded setup in the console — secure step runs with your identity provider"
        actions={
          <Button
            variant="ghost"
            icon="close"
            disabled={busy}
            onClick={() => {
              setBusy(false)
              navigate('/settings/security')
            }}
          >
            Cancel
          </Button>
        }
      />

      {blockedByRival ? (
        <div style={{ marginBottom: 'var(--w7-s4)' }} data-testid="mfa-enroll-rival-block">
          <StateBlock
            icon="warning"
            tone="error"
            title={`${rivalTitle} is already on`}
            actions={
              <Button variant="primary" onClick={() => navigate('/settings/security')}>
                Turn off {rivalTitle}
              </Button>
            }
          >
            Only one sign-in method at a time. Remove {rivalTitle} on the security page, then set up{' '}
            {method.title}.
          </StateBlock>
        </div>
      ) : null}

      {passkeyHostBroken ? (
        <div style={{ marginBottom: 'var(--w7-s4)' }}>
          <StateBlock icon="warning" tone="error" title="Passkeys need localhost">
            Use <code>http://localhost:5273</code> (not 127.0.0.1) and Keycloak at{' '}
            <code>http://localhost:8081</code>. Otherwise the browser reports “invalid domain”.
          </StateBlock>
        </div>
      ) : null}

      {justBack || enrolledReturn ? (
        <div style={{ marginBottom: 'var(--w7-s4)' }}>
          <StateBlock icon="check" title="Setup finished or cancelled">
            If you enrolled successfully, this method is Active on the security page. You can run
            setup again or go back to all methods.
          </StateBlock>
        </div>
      ) : null}

      <Card title="How it works" style={{ marginBottom: 'var(--w7-s4)' }}>
        <p style={{ margin: '0 0 var(--w7-s3)', color: 'var(--w7-fg-muted)', lineHeight: 1.5 }}>
          {method.body}
        </p>
        <ol style={{ margin: 0, paddingLeft: '1.25rem', lineHeight: 1.6, color: 'var(--w7-fg)' }}>
          {(method.steps ?? []).map((s) => (
            <li key={s} style={{ marginBottom: 8 }}>
              {s}
            </li>
          ))}
        </ol>
      </Card>

      {!method.kcAction ? (
        <StateBlock icon="info" title="Not available yet">
          {method.statusLabel}. This method will appear here when the platform connector is ready.
        </StateBlock>
      ) : (
        <div className="w7-row" style={{ gap: 'var(--w7-s3)', flexWrap: 'wrap' }}>
          <Button
            variant="primary"
            icon="shield"
            busy={busy}
            onClick={() => void start()}
            disabled={busy || passkeyHostBroken || blockedByRival}
          >
            {busy ? 'Opening secure setup…' : 'Continue setup'}
          </Button>
          <Button
            variant="secondary"
            disabled={busy}
            onClick={() => {
              setBusy(false)
              navigate('/settings/security')
            }}
          >
            Back
          </Button>
        </div>
      )}

      <p className="w7-dim" style={{ marginTop: 'var(--w7-s5)', fontSize: 13, maxWidth: 40 * 16 }}>
        The next screen is your identity provider’s enroll step (QR, fingerprint, or key). When it
        finishes you return to the security hub — Genesis never sees your fingerprint or
        authenticator secret. <Link to="/settings/security">All methods</Link>
      </p>
    </>
  )
}
